import {useState} from "react";
import DsDevelopmentRoster from "@/components/DsScouting/Roster";
import {
	dsCandidateDepartment,
	dsUpcomingObservations,
} from "@/utils/dsScouting";
import {formatAverage} from "@/utils/scoutingReports";
import moment from "moment";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsChips,
	DsEmpty,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissingSources,
	DsSection,
	DsSkeleton,
	DsStatus,
	DsTable,
} from "@/components/DsKit";

import {useClubSnapshot} from "@/utils/hooks";
import {
	DEPARTMENTS,
	PIPELINE_STAGES,
	buildNeeds,
	playerName,
	matchDateLabel,
} from "@/utils/clubSnapshot";

const STAGE_STATUS = {
	watching: "none",
	evaluating: "warn",
	target: "alert",
	negotiating: "ok",
};

function DsScoutingPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
		error,
	} = useClubSnapshot({handleNotification});

	const [selectedRoster, setSelectedRoster] = useState("");
	const pipeline = s?.pipeline || [];
	const count = (id) => pipeline.filter((e) => e.status === id).length;
	const stageOrder = Object.fromEntries(
		PIPELINE_STAGES.map((st, i) => [st.id, i]),
	);
	const rows = pipeline
		.map((e) => ({...e, id: e.playerId, player: e.player}))
		.sort((a, b) => (stageOrder[b.status] || 0) - (stageOrder[a.status] || 0));

	const observed = dsUpcomingObservations(s?.observedMatches || []);

	// Fabbisogni della rosa incrociati con i candidati in pipeline
	const needs = s ? buildNeeds(s) : [];
	const neededDeps = DEPARTMENTS.filter((d) =>
		needs.some((n) => n.department.id === d.id),
	);
	const coverage = neededDeps.map((d) => ({
		id: d.id,
		department: d,
		teams: needs
			.filter((n) => n.department.id === d.id)
			.map((n) => n.team.name),
		candidates: rows.filter(
			(r) => r.player && dsCandidateDepartment(r.player) === d.id,
		).length,
	}));

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Scouting"
					subtitle="Obiettivi di mercato e osservazioni"
					snapshot={s}
					scope={scope}
					area="scouting"
					onReload={reload}
					reloading={loading}
					actions={
						<button
							className="ds-btn ds-btn--ghost"
							onClick={() => navigate("/scouting/pipeline")}
						>
							Apri pipeline completa
						</button>
					}
				/>
				<DsSkeleton visible={loading && !s} />
				{error && (
					<DsEmpty>
						Dati scouting non disponibili o accesso non consentito. Aggiorna per
						riprovare.
					</DsEmpty>
				)}
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsKpiRow>
							{PIPELINE_STAGES.map((st) => (
								<DsKpi key={st.id} label={st.label} value={count(st.id)} />
							))}
							<DsKpi
								label="Gare da osservare"
								value={observed.length}
								sub="prossimi 14 giorni"
							/>
						</DsKpiRow>

						<DsSection
							title="Fabbisogni coperti dallo scouting?"
							hint="Reparti scoperti nelle rose e quanti candidati ci sono in pipeline per quel ruolo"
						>
							{rows.some((r) => !dsCandidateDepartment(r.player)) && (
								<p className="ds-muted">
									{rows.filter((r) => !dsCandidateDepartment(r.player)).length}{" "}
									candidati senza reparto classificabile, esclusi dal confronto.
								</p>
							)}
							{coverage.length ? (
								<DsTable
									columns={[
										{
											key: "dep",
											label: "Reparto",
											render: (c) => <b>{c.department.label}</b>,
										},
										{
											key: "teams",
											label: "Squadre con esigenza",
											render: (c) => c.teams.join(", "),
										},
										{
											key: "candidates",
											label: "Candidati",
											align: "center",
											render: (c) => (
												<DsStatus
													status={c.candidates ? "ok" : "alert"}
													label={c.candidates || "Nessuno"}
												/>
											),
										},
									]}
									rows={coverage}
								/>
							) : (
								<DsEmpty>Nessun fabbisogno aperto nelle rose.</DsEmpty>
							)}
						</DsSection>

						<DsSection
							title="Giocatori in pipeline"
							hint="Dalla trattativa al semplice monitoraggio. Dati del club, indipendenti dal filtro rosa."
						>
							<DsTable
								columns={[
									{
										key: "player",
										label: "Giocatore",
										render: (r) => (
											<>
												<div className="ds-strong">
													{r.player ? playerName(r.player) : "—"}
												</div>
												<div className="ds-muted">
													{r.player?.goalkeeper
														? "Portiere"
														: r.player?.internalRole?.name ||
															r.player?.primaryRole ||
															""}
													{r.player?.dateOfBirth
														? " · " + moment(r.player.dateOfBirth).year()
														: ""}
												</div>
											</>
										),
									},
									{
										key: "stage",
										label: "Fase",
										render: (r) => (
											<DsStatus
												status={STAGE_STATUS[r.status] || "none"}
												label={
													PIPELINE_STAGES.find((st) => st.id === r.status)
														?.label || "—"
												}
											/>
										),
									},
									{
										key: "note",
										label: "Nota",
										render: (r) =>
											r.note || <span className="ds-muted">—</span>,
									},
									{
										key: "added",
										label: "Inserito",
										render: (r) =>
											r.addedAt ? moment(r.addedAt).format("D MMM") : "—",
									},
								]}
								rows={rows}
								onRowClick={(r) => navigate("/ds/giocatore/" + r.playerId)}
								empty="Nessun obiettivo. Aggiungi i giocatori dallo Scouting."
							/>
						</DsSection>

						<DsSection
							title="Relazioni scouting"
							hint="Ultime relazioni della stagione, condivise con il modulo scouting."
						>
							<DsTable
								columns={[
									{
										key: "player",
										label: "Giocatore",
										render: (r) => playerName(r.player),
									},
									{
										key: "reportDate",
										label: "Data",
										render: (r) => moment(r.reportDate).format("DD/MM/YYYY"),
									},
									{
										key: "average",
										label: "Media",
										render: (r) => formatAverage(r.average),
									},
									{
										key: "author",
										label: "Autore",
										render: (r) => r.author?.name || "—",
									},
								]}
								rows={(s.scoutingReports || []).slice(0, 10)}
								onRowClick={(r) => navigate("/scouting/relazioni/" + r.id)}
								empty="Nessuna relazione nella stagione selezionata."
							/>
						</DsSection>
						<DsSection
							title="Roster sviluppo"
							hint="Schieramento confermato sul server, in sola lettura."
						>
							{(s.developmentRosters || []).length ? (
								<>
									{s.developmentRosters.length > 1 && (
										<DsChips
											options={s.developmentRosters.map((r) => ({
												value: r.id,
												label: r.name,
											}))}
											value={
												s.developmentRosters.some(
													(r) => r.id === selectedRoster,
												)
													? selectedRoster
													: s.developmentRosters[0].id
											}
											onChange={setSelectedRoster}
										/>
									)}
									<DsDevelopmentRoster
										id={
											s.developmentRosters.some((r) => r.id === selectedRoster)
												? selectedRoster
												: s.developmentRosters[0].id
										}
										revision={s.loadedAt}
									/>
								</>
							) : (
								<DsEmpty>
									Nessuna rosa sviluppo disponibile nella stagione.
								</DsEmpty>
							)}
						</DsSection>
						<DsSection
							title="Gare da osservare"
							hint="Prossimi 14 giorni"
							action={<DsLink to="/scouting/planning">Planning</DsLink>}
						>
							{observed.length ? (
								<div className="ds-list">
									{observed.map((m) => (
										<div
											key={m.id}
											className="ds-list__item is-clickable"
											onClick={() => navigate("/scouting/planning/" + m.id)}
										>
											<div className="ds-list__main">
												<div className="ds-list__title">
													{m.homeClub || m.homeTeamName} —{" "}
													{m.awayClub || m.awayTeamName}
												</div>
												<div className="ds-list__sub">
													{m.competitionName || m.matchType || ""}
												</div>
											</div>
											<div className="ds-list__aside">
												{matchDateLabel(m, "ddd D/MM")}
											</div>
										</div>
									))}
								</div>
							) : (
								<DsEmpty>Nessuna gara pianificata.</DsEmpty>
							)}
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsScoutingPage;
